import React, {useEffect, useState} from "react";
import {useParams, useNavigate} from "react-router-dom";
import {doc, getDoc} from "firebase/firestore";
import {db} from '../config/firebase';
import {editRecipe} from "./recipeService";
import {getStorage, ref, uploadBytes, getDownloadURL} from "firebase/storage";
import './RecipeForm.css';
import { compressImage } from '../utils/compressImage';

const EditRecipe = () => {
    const {recipeId} = useParams();
    const navigate = useNavigate();
    const [recipe, setRecipe] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        const fetchRecipe = async () => {
            const recipeSnap = await getDoc(doc(db, 'recetas', recipeId));
            if (recipeSnap.exists()) {
                const data = recipeSnap.data();
                setRecipe({
                    ...data,
                    // Normalizar campo antiguo al nuevo para documentos viejos
                    tiempoEstimado: data.tiempoEstimado ?? data.tiempoPreparacion ?? 0,
                });
                setImagePreview(data.imagen || null);
            }
        };
        fetchRecipe();
    }, [recipeId]);

    const handleIngredientChange = (index, e) => {
        const newIngredients = [...recipe.ingredientes];
        newIngredients[index][e.target.name] = e.target.value;
        setRecipe({...recipe, ingredientes: newIngredients});
   };

    const addIngredient = () => {
        setRecipe({...recipe, ingredientes: [...recipe.ingredientes, {nombre: '', cantidad: '', unidad: ''}]});
   };

    const removeIngredient = (index) => {
        const newIngredients = recipe.ingredientes.filter((_, i) => i !== index);
        setRecipe({...recipe, ingredientes: newIngredients});
   };

    const handleInstructionChange = (index, e) => {
        const newInstructions = [...recipe.instrucciones];
        newInstructions[index] = e.target.value;
        setRecipe({...recipe, instrucciones: newInstructions});
   };

    const addInstruction = () => {
        setRecipe({...recipe, instrucciones: [...recipe.instrucciones, '']});
   };

    const removeInstruction = (index) => {
        const newInstructions = recipe.instrucciones.filter((_, i) => i !== index);
        setRecipe({...recipe, instrucciones: newInstructions});
   };

    const handleImageChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            setUploading(true);
            setImagePreview(URL.createObjectURL(file));
            try {
                const compressed = await compressImage(file);  // <- comprimir
                const storage = getStorage();
                const storageRef = ref(storage, `recetas/${Date.now()}_${file.name}`);
                await uploadBytes(storageRef, compressed);      // <- subir comprimida
                const url = await getDownloadURL(storageRef);
                setRecipe(r => ({ ...r, imagen: url }));
            } catch (err) {
                console.error('Error subiendo imagen:', err);
            } finally {
                setUploading(false);
            }
        }
   };

    const handleChange = (e) => {
        setRecipe({...recipe, [e.target.name]: e.target.value});
   };

    const handleSubmit = async (e) => {
        e.preventDefault();
        await editRecipe(recipeId, recipe);
        navigate(`/recipe/${recipeId}`);
   };

    if (!recipe) return <div>Cargando...</div>;

    return (
        <form onSubmit={handleSubmit} className="recipe-form">
            <input
                name="nombre"
                value={recipe.nombre}
                onChange={handleChange}
                placeholder="Nombre de la receta"
                required
            />
            <h3>Ingredientes</h3>
            {recipe.ingredientes.map((ingredient, index) => (
                <div key={index} className="recipe-form-ingrediente">
                    <input
                        name="nombre"
                        value={ingredient.nombre}
                        onChange={(e) => handleIngredientChange(index, e)}
                        placeholder="Ingrediente"
                        required
                    />
                    <input
                        name="cantidad"
                        value={ingredient.cantidad}
                        onChange={(e) => handleIngredientChange(index, e)}
                        placeholder="Cantidad"
                        type="number"
                    />
                    <input
                        name="unidad"
                        value={ingredient.unidad}
                        onChange={(e) => handleIngredientChange(index, e)}
                        placeholder="Uds"
                    />
                    {recipe.ingredientes.length > 1 && (
                        <button
                            type="button"
                            onClick={() => removeIngredient(index)}
                            title="Eliminar ingrediente"
                        >-</button>
                    )}
                    {index === recipe.ingredientes.length - 1 && (
                        <button
                            type="button"
                            onClick={addIngredient}
                            title="Agregar ingrediente"
                        >+</button>
                    )}
                </div>
            ))}
            <h3>Instrucciones</h3>
            {recipe.instrucciones.map((instruction, index) => (
                <div key={index} className="recipe-form-instruccion">
                    <textarea
                        value={instruction}
                        onChange={(e) => handleInstructionChange(index, e)}
                        placeholder="Instrucción"
                        required
                    />
                    {recipe.instrucciones.length > 1 && (
                        <button
                            type="button"
                            onClick={() => removeInstruction(index)}
                            title="Eliminar instrucción"
                        >-</button>
                    )}
                    {index === recipe.instrucciones.length - 1 && (
                        <button
                            type="button"
                            onClick={addInstruction}
                            title="Agregar instrucción"
                        >+</button>
                    )}
                </div>
            ))}

            {/* --- NUEVOS CAMPOS: Tiempo estimado y Dificultad (edición) --- */}
            <h3>Tiempo estimado</h3>
            <div className="recipe-form-time-row">
              <input
                name="tiempoEstimado"
                type="number"
                min="0"
                value={recipe.tiempoEstimado || ''}
                onChange={e => setRecipe({...recipe, tiempoEstimado: e.target.value === '' ? '' : Number(e.target.value)})}
                placeholder="Tiempo estimado"
              />
              <span className="time-unit">minutos</span>
            </div>

            <h3>Dificultad</h3>
            <select
              name="dificultad"
              value={recipe.dificultad || ''}
              onChange={e => setRecipe({...recipe, dificultad: e.target.value})}
            >
              <option value="">Selecciona</option>
              <option value="fácil">Fácil</option>
              <option value="media">Media</option>
              <option value="difícil">Difícil</option>
            </select>
            {/* --- FIN NUEVOS CAMPOS --- */}

            <h3>Privacidad</h3>
            <select
                value={recipe.privacidad || 'publica'}
                onChange={e => setRecipe({...recipe, privacidad: e.target.value})}
            >
                <option value="publica">🌍 Pública (visible para todos)</option>
                <option value="seguidores">👥 Solo para seguidores</option>
                <option value="privada">🔒 Solo para mí</option>
            </select>

            <h3>Imagen</h3>
            <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
            />
            {imagePreview && (
                <img src={imagePreview} alt="Previsualización" />
            )}
            <button type="submit" disabled={uploading} title="Guardar cambios" className="publicar-btn">
                Guardar cambios
            </button>
            {uploading && (
                <div className="modal-loader">
                    <div className="modal-loader-content">
                        <p>Subiendo imagen...</p>
                    </div>
                </div>
            )}
        </form>
    );
};

export default EditRecipe;